import { useCallback, useEffect, useState } from 'react'

import { AUTH_EXPIRED_EVENT, getJob, type Job, type JobStatus } from './api'
import { ApiError } from './problem'

/**
 * Watching a job until it stops being one.
 *
 * ── A PAUSE IS NOT A FAILURE, AND THE DIFFERENCE IS THE WHOLE POINT ─────────
 *
 * §2.8: "pause with a recoverable message on network failure". A job that
 * `failed` is a conclusion the backend reached about a recording. A poll that
 * could not reach the backend is a statement about the network, and rendering it
 * as a failed job tells a user their recording is lost when it is very likely
 * still being scored. So a transport error sets `paused` and offers a retry; it
 * never writes a terminal state, and it never clears the job already known.
 *
 * A Problem answer — a 403, a 404, a 500 with an envelope — is the opposite: the
 * server was reached and it refused. That is `error`, and no amount of retrying
 * changes it.
 *
 * ── AND STOPPING MATTERS AS MUCH AS POLLING ─────────────────────────────────
 *
 * The loop stops on a terminal status, on unmount, and on `AUTH_EXPIRED_EVENT`.
 * The last one is not decoration: a poll that keeps running after a session
 * expires fires a 401 every few seconds, each one re-dispatching the event that
 * signs the user out, against an account that is already gone.
 */

/** 1s, 2s, 4s, then 8s for as long as it takes. */
export const POLL_BACKOFF_MS = [1_000, 2_000, 4_000, 8_000] as const

/**
 * The three statuses that mean the job has stopped.
 *
 * `pending` and `running` are the only pre-terminal ones. Note these are JOB
 * statuses: `queued` and `processing` are RECORDING statuses, are reserved
 * compatibility values with no writer, and the spec's §2.5 conflates the two
 * vocabularies. Waiting for a job to reach "processing" would wait forever.
 */
const TERMINAL: ReadonlySet<JobStatus> = new Set<JobStatus>([
  'succeeded',
  'failed',
  'interrupted',
])

export function isTerminalJob(status: JobStatus): boolean {
  return TERMINAL.has(status)
}

export interface JobPoll {
  /** The last answer received. Kept through a pause. */
  job: Job | null
  /** A job id is known and its status is not terminal. */
  isPolling: boolean
  /** The network failed. Recoverable, and `retry` is offered. */
  isPaused: boolean
  /** The server answered and refused. Not recoverable by retrying. */
  error: unknown
  retry: () => void
}

export function useJobPolling(jobId: string | null): JobPoll {
  const [job, setJob] = useState<Job | null>(null)
  const [isPaused, setPaused] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [attempt, setAttempt] = useState(0)

  const retry = useCallback(() => setAttempt((n) => n + 1), [])

  // A different job is a different watch. Render-time adjustment, the same
  // pattern and for the same reason as `useResource`: an effect runs a frame
  // later, which is long enough to paint one job's phase under another's id.
  const [lastId, setLastId] = useState(jobId)
  if (lastId !== jobId) {
    setLastId(jobId)
    setJob(null)
    setPaused(false)
    setError(null)
  }

  useEffect(() => {
    if (jobId === null) return

    const controller = new AbortController()
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined

    const stop = () => {
      cancelled = true
      controller.abort()
      if (timer !== undefined) clearTimeout(timer)
    }
    // A session that has expired is not a session to poll under.
    window.addEventListener(AUTH_EXPIRED_EVENT, stop)

    void (async () => {
      let step = 0
      for (;;) {
        try {
          const next = await getJob(jobId, { signal: controller.signal })
          if (cancelled) return
          setJob(next)
          setPaused(false)
          setError(null)
          if (isTerminalJob(next.status)) return
        } catch (err) {
          if (cancelled) return
          if (err instanceof ApiError) {
            // The server answered and refused. Retrying will not help.
            setError(err)
          } else {
            // A TypeError from fetch, or the client's own timeout. The job is
            // very probably still running; say so and offer to look again.
            setPaused(true)
          }
          return
        }

        // Wait the CURRENT step, then lengthen. Incrementing first skips the
        // 1s rung entirely and makes the first gap 2s -- which no assertion
        // about "backs off" would notice, because it still backs off.
        await new Promise<void>((resolve) => {
          timer = setTimeout(resolve, POLL_BACKOFF_MS[step])
        })
        step = Math.min(step + 1, POLL_BACKOFF_MS.length - 1)
        if (cancelled) return
      }
    })()

    return () => {
      window.removeEventListener(AUTH_EXPIRED_EVENT, stop)
      stop()
    }
  }, [jobId, attempt])

  const isPolling =
    jobId !== null && !isPaused && error === null && (job === null || !isTerminalJob(job.status))

  return { job, isPolling, isPaused, error, retry }
}
